import { Controller } from '@hotwired/stimulus'
import { watch } from 'vue'
import getPiniaInstance from '@app/shared/pinia/instance'
import { useReportRowHighlight } from '@app/application/report/store/use-report-row-highlight'

// Applied per-cell (each sets its own white background; the first column is
// sticky) as inline `important` styles, not utility classes: styled rows
// (report-template-style-*) have background AND text colour set `!important` by
// legacy CSS (HighlightStyleHelper) that out-specifies a single-class utility.
// An inline `!important` wins regardless of specificity. We restore a readable
// text colour only on those styled rows (their forced white text is unreadable
// on the light fill); other rows keep their own colours. Revert to the
// `!tw-bg-sf-success-subtle-2` utility once that legacy `!important` is gone.
// Fill/accent are the Tailwind `sf-success` scale (no matching CSS var); the
// text colour is $report-table-text-color from reports.scss.
const HIGHLIGHT_BG = '#d1fae5' // sf-success-subtle-2
const HIGHLIGHT_ACCENT = '4px solid #059669' // sf-success (DEFAULT)
const HIGHLIGHT_TEXT = '#2c384b' // $report-table-text-color

const DRAWER_SELECTOR = '[data-behaviour="reports/graphs-left-drawer"]'

// Mounted on table#report. Subscribes to the row-highlight store and renders
// the visual treatment for each matching row — by data-identifier (referenced
// report elements) and by data-account-id (referenced accounts). A graph metric
// can reference several of each, so multiple rows may be highlighted at once;
// the topmost is scrolled into view. Expands any collapsed groups so the rows
// are reachable, and clears the highlight on outside clicks. Hidden rows
// (data-row-hidden="true") are treated as not present.
export default class extends Controller {
  private store = useReportRowHighlight(getPiniaInstance())
  private highlightedRows: HTMLTableRowElement[] = []
  private stopWatch: (() => void) | null = null
  private dispatchingSyntheticClick = false

  connect(): void {
    this.stopWatch = watch(
      () => [this.store.identifiers, this.store.accountIds] as const,
      () => this.handleTargetsChange(),
      { immediate: true }
    )
    document.addEventListener('click', this.handleDocumentClick, true)
  }

  disconnect(): void {
    this.stopWatch?.()
    this.stopWatch = null
    document.removeEventListener('click', this.handleDocumentClick, true)
    this.clearVisualHighlight()
  }

  // Whether the store currently targets any rows (by element identifier or
  // account id).
  private get hasTarget(): boolean {
    return this.store.identifiers.length > 0 || this.store.accountIds.length > 0
  }

  private handleTargetsChange(): void {
    this.clearVisualHighlight()
    if (!this.hasTarget) return

    const { identifiers, accountIds } = this.store
    const rows = this.findRows(identifiers, accountIds)
    if (rows.length === 0) return

    rows.forEach((row) => {
      this.expandAncestorCollapsedGroups(row)
      // The matched row may itself be a collapsed group/subtotal (a metric
      // often maps to one), so expand it too — not just its ancestors.
      this.expandCollapsedGroup(row)
      this.applyVisualHighlight(row)
    })
    this.highlightedRows = rows

    // Scroll to the topmost highlighted row so the referenced rows are in view.
    rows[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  // Matching rows in DOM order (so the first is the topmost), de-duplicated and
  // excluding rows hidden via visibility settings. A row matches when its
  // data-identifier is a referenced element or its data-account-id is a
  // referenced account.
  private findRows(
    identifiers: string[],
    accountIds: number[]
  ): HTMLTableRowElement[] {
    const wantedIdentifiers = new Set(identifiers)
    const wantedAccountIds = new Set(accountIds.map(String))
    const rows = this.element.querySelectorAll<HTMLTableRowElement>(
      'tr[data-identifier], tr[data-account-id]'
    )
    const matched: HTMLTableRowElement[] = []
    for (const row of Array.from(rows)) {
      if (row.dataset.rowHidden === 'true') continue
      const { identifier, accountId } = row.dataset
      const isMatch =
        (identifier !== undefined && wantedIdentifiers.has(identifier)) ||
        (accountId !== undefined && wantedAccountIds.has(accountId))
      if (isMatch) matched.push(row)
    }
    return matched
  }

  private expandAncestorCollapsedGroups(row: HTMLTableRowElement): void {
    const targetLevel = parseInt(row.dataset.level ?? '0', 10)
    if (Number.isNaN(targetLevel) || targetLevel <= 0) return

    const ancestors: HTMLTableRowElement[] = []
    let current = row.previousElementSibling as HTMLTableRowElement | null
    let lookingForLevel = targetLevel - 1

    while (current && lookingForLevel >= 0) {
      const currentLevel = parseInt(current.dataset.level ?? '', 10)
      if (!Number.isNaN(currentLevel) && currentLevel <= lookingForLevel) {
        ancestors.unshift(current)
        lookingForLevel = currentLevel - 1
      }
      current = current.previousElementSibling as HTMLTableRowElement | null
    }

    // Expand outermost first so inner-collapse state survives the walk.
    for (const ancestor of ancestors) {
      this.expandCollapsedGroup(ancestor)
    }
  }

  // Reuse the existing group-toggle behaviour (report-rows.js) by clicking the
  // collapsed group row. Flag the synthetic dispatch so handleDocumentClick
  // doesn't clear the highlight we're about to apply.
  private expandCollapsedGroup(group: HTMLTableRowElement): void {
    if (group.dataset.collapsed !== 'true') return
    this.dispatchingSyntheticClick = true
    try {
      group.click()
    } finally {
      this.dispatchingSyntheticClick = false
    }
  }

  private applyVisualHighlight(row: HTMLTableRowElement): void {
    const styled = this.hasStyledBackground(row)
    const cells = row.querySelectorAll<HTMLTableCellElement>('th, td')
    cells.forEach((cell, index) => {
      cell.style.setProperty('background-color', HIGHLIGHT_BG, 'important')
      if (styled) cell.style.setProperty('color', HIGHLIGHT_TEXT, 'important')
      if (index === 0) {
        cell.style.setProperty('border-left', HIGHLIGHT_ACCENT, 'important')
      }
    })
  }

  private clearVisualHighlight(): void {
    this.highlightedRows.forEach((row) => {
      const cells = row.querySelectorAll<HTMLTableCellElement>('th, td')
      cells.forEach((cell, index) => {
        cell.style.removeProperty('background-color')
        cell.style.removeProperty('color')
        if (index === 0) cell.style.removeProperty('border-left')
      })
    })
    this.highlightedRows = []
  }

  // Styled rows (subtotals/titles/highlights) carry a report-template-style-*
  // class that forces white text via legacy CSS, so we restore a readable text
  // colour on them — but not on plain rows, which keep their own colours.
  private hasStyledBackground(row: HTMLTableRowElement): boolean {
    return Array.from(row.classList).some((name) =>
      name.startsWith('report-template-style-')
    )
  }

  private handleDocumentClick = (event: MouseEvent): void => {
    if (!this.hasTarget) return
    // Skip our own synthetic clicks fired to expand collapsed group rows —
    // they'd otherwise clear the highlight we're about to apply.
    if (this.dispatchingSyntheticClick) return
    const target = event.target as HTMLElement | null
    if (!target) return
    // Chart clicks (drawer) set a new highlight; don't clear on those.
    if (target.closest(DRAWER_SELECTOR)) return
    // Clicking one of the currently-highlighted rows is a no-op (don't clear).
    const row = target.closest<HTMLTableRowElement>(
      'tr[data-identifier], tr[data-account-id]'
    )
    if (row && this.highlightedRows.includes(row)) return
    this.store.clear()
  }
}
